import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { X, Loader2, CreditCard, Minus, Plus, Clock } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { SeatMap, availToSections, formatSeat } from "./SeatMap";

export default function CheckoutModal({ slug, product, checkoutFees, accessPassword, buttonColor, buttonTextColor, onClose }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const fees = checkoutFees || { live: false, pass_fees: false, commission_pct: 0, commission_fixed: 0 };
  const tiers = product.ticket_tiers || [];
  const reserved = !!product.reserved_seating;
  const [liveAvail, setLiveAvail] = useState(product.seat_availability || {});
  const [qty, setQty] = useState(() => Object.fromEntries(tiers.map((t) => [t.id, 0])));
  const [seats, setSeats] = useState({}); // tierId -> [seat labels]
  const [names, setNames] = useState({}); // `${tierId}-${i}` -> name
  const [buyer, setBuyer] = useState({ buyer_name: "", buyer_email: "", buyer_phone: "" });
  // Prefill from the signed-in fan's account so their tickets land in their history.
  useEffect(() => {
    if (user?.role === "fan") {
      setBuyer((b) => ({ ...b, buyer_name: b.buyer_name || user.name || "", buyer_email: b.buyer_email || user.email || "" }));
    }
  }, [user]);
  const [loading, setLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(null); // {code,type,value}
  const [checking, setChecking] = useState(false);
  const [hold, setHold] = useState(null); // {token, expiresAt}
  const [secsLeft, setSecsLeft] = useState(0);
  const holdTokenRef = useRef("");

  const qOf = (t) => (reserved ? (seats[t.id]?.length || 0) : (qty[t.id] || 0));
  const remaining = (t) => (t.capacity > 0 ? Math.max(0, t.capacity - (t.sold || 0)) : Infinity);

  const refetchAvail = async () => {
    try {
      const { data } = await api.get(`/store/${slug}/events/${product.slug}`, { params: { hold_token: holdTokenRef.current || "" } });
      if (data?.product?.seat_availability) setLiveAvail(data.product.seat_availability);
    } catch { /* ignore */ }
  };

  const toggleSeat = (t, label, taken) => {
    if (taken) return;
    setSeats((prev) => {
      const cur = prev[t.id] || [];
      const next = cur.includes(label) ? cur.filter((s) => s !== label) : [...cur, label];
      return { ...prev, [t.id]: next };
    });
  };

  // Reserved seating: hold selected seats server-side (debounced) so nobody else grabs them.
  useEffect(() => {
    if (!reserved) return;
    const items = Object.entries(seats).filter(([, arr]) => arr.length).map(([tid, arr]) => ({ tier_id: tid, seats: arr }));
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post(`/store/${slug}/hold`, { items, hold_token: holdTokenRef.current });
        holdTokenRef.current = data.hold_token || "";
        if (data.hold_token && data.expires_at) { setHold({ token: data.hold_token, expiresAt: new Date(data.expires_at).getTime() }); }
        else { setHold(null); }
      } catch (e) {
        if (e?.response?.status === 409) {
          toast({ title: "Seat just taken", description: e.response.data?.detail || "Please pick another.", variant: "destructive" });
          try {
            const { data } = await api.get(`/store/${slug}/events/${product.slug}`, { params: { hold_token: holdTokenRef.current || "" } });
            const av = data?.product?.seat_availability || {};
            setLiveAvail(av);
            setSeats((prev) => {
              const next = {};
              for (const [tid, labels] of Object.entries(prev)) {
                const takenSet = new Set((av[tid]?.seats || []).filter((x) => x.taken).map((x) => x.label));
                next[tid] = labels.filter((l) => !takenSet.has(l));
              }
              return next;
            });
          } catch { /* ignore */ }
        }
      }
    }, 500);
    return () => clearTimeout(id);
  }, [seats, reserved, slug]);

  // Countdown + expiry handling
  useEffect(() => {
    if (!hold) { setSecsLeft(0); return; }
    const tick = () => {
      const left = Math.max(0, Math.round((hold.expiresAt - Date.now()) / 1000));
      setSecsLeft(left);
      if (left <= 0) {
        holdTokenRef.current = "";
        setHold(null); setSeats({}); refetchAvail();
        toast({ title: "Hold expired", description: "Your seats were released — please pick again.", variant: "destructive" });
      }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [hold]);

  // Poll availability so other buyers' holds/purchases appear live
  useEffect(() => {
    if (!reserved) return;
    const iv = setInterval(refetchAvail, 20000);
    return () => clearInterval(iv);
  }, [reserved]);

  // Release hold if the modal closes without paying
  useEffect(() => () => {
    if (holdTokenRef.current) api.delete(`/store/${slug}/hold/${holdTokenRef.current}`).catch(() => {});
  }, [slug]);

  const setQ = (t, delta) => {
    setQty((q) => {
      const max = remaining(t);
      const next = Math.min(Math.max(0, (q[t.id] || 0) + delta), max === Infinity ? 20 : max);
      return { ...q, [t.id]: next };
    });
  };

  const tierSubtotal = (t) => {
    if (reserved && t.table_mode && (t.table_discount_pct || 0) > 0) {
      const sel = seats[t.id] || [];
      const byTable = {};
      sel.forEach((l) => { const k = l.split("S")[0]; (byTable[k] = byTable[k] || []).push(l); });
      return Object.values(byTable).reduce((sum, ss) => {
        const full = t.seats_per_table > 0 && ss.length >= t.seats_per_table;
        return sum + ss.length * (full ? t.price * (1 - t.table_discount_pct / 100) : t.price);
      }, 0);
    }
    return qOf(t) * t.price;
  };
  const subtotal = useMemo(
    () => tiers.reduce((s, t) => s + tierSubtotal(t), 0),
    [qty, seats, tiers]
  );
  const listTotal = useMemo(() => tiers.reduce((s, t) => s + qOf(t) * t.price, 0), [qty, seats, tiers]);
  const tableSaving = Math.round((listTotal - subtotal) * 100) / 100;
  const totalTickets = tiers.reduce((s, t) => s + qOf(t), 0);
  const total = useMemo(() => {
    if (!discount) return subtotal;
    const matchSub = tiers.reduce((s, t) => s + ((!discount.tier_id || t.id === discount.tier_id) ? tierSubtotal(t) : 0), 0);
    const other = subtotal - matchSub;
    const discMatch = discount.type === "percent" ? matchSub * (1 - discount.value / 100) : Math.max(0, matchSub - discount.value);
    return Math.max(0, other + discMatch);
  }, [discount, subtotal, qty, seats, tiers]);

  const bookingFee = useMemo(() => {
    if (!fees.pass_fees || totalTickets === 0) return 0;
    return Math.round((total * (fees.commission_pct || 0) / 100 + (fees.commission_fixed || 0) * totalTickets) * 100) / 100;
  }, [fees, total, totalTickets]);
  const grandTotal = Math.round((total + bookingFee) * 100) / 100;

  const applyCode = async () => {
    if (!discountCode.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.post(`/store/${slug}/apply-discount`, { code: discountCode, product_id: product.id });
      if (data.valid) {
        setDiscount({ code: data.code, type: data.type, value: data.value, tier_id: data.tier_id || "", product_id: data.product_id || "" });
        toast({ title: "Discount applied", description: data.tier_name ? `Code ${data.code} — ${data.tier_name} only.` : `Code ${data.code} is valid.` });
      } else {
        setDiscount(null);
        toast({ title: "Invalid code", description: data.message || "Not valid", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Could not apply code", variant: "destructive" });
    } finally { setChecking(false); }
  };

  const pay = async (e) => {
    e.preventDefault();
    if (totalTickets === 0) { toast({ title: "Select tickets", description: "Add at least one ticket.", variant: "destructive" }); return; }
    const items = tiers
      .filter((t) => qOf(t) > 0)
      .map((t) => {
        const sel = reserved ? (seats[t.id] || []) : [];
        return {
          tier_id: t.id,
          attendees: reserved
            ? sel.map((label) => (names[`${t.id}-seat-${label}`] || buyer.buyer_name || "Guest").trim())
            : Array.from({ length: qty[t.id] }, (_, i) => (names[`${t.id}-${i}`] || buyer.buyer_name || "Guest").trim()),
          seats: sel,
        };
      });
    setLoading(true);
    try {
      const { data } = await api.post(`/store/${slug}/checkout`, {
        ...buyer, items, discount_code: discount ? discount.code : "", origin: window.location.origin,
        hold_token: holdTokenRef.current, access_password: accessPassword || "",
        promo: new URLSearchParams(window.location.search).get("promo") || "",
      });
      holdTokenRef.current = ""; // consumed
      if (data.mode === "stripe" && data.url) { window.location.href = data.url; return; }
      toast({ title: "Booking confirmed", description: `${data.count} ticket${data.count !== 1 ? "s" : ""} issued!` });
      nav(`/voucher/${data.code}?new=1`);
    } catch (err) {
      toast({ title: "Booking failed", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="checkout-modal">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-[#16243b] text-lg">{product.title}</h3>
            <p className="text-[#8a97a8] text-sm">Select your tickets</p>
          </div>
          <button onClick={onClose} data-testid="checkout-close"><X className="text-[#5a6b80]" /></button>
        </div>
        <form onSubmit={pay} className="p-6 space-y-5">
          {/* Reserved seating: unified visual map + per-seat attendee names */}
          {reserved && (
            <div className="space-y-4">
              {hold && secsLeft > 0 && (
                <div className="flex items-center justify-center gap-2 text-sm font-medium rounded-xl bg-[#fff7ed] border border-[#fed7aa] text-[#b45309] py-2" data-testid="hold-countdown">
                  <Clock size={15} /> Seats held for {Math.floor(secsLeft / 60)}:{String(secsLeft % 60).padStart(2, "0")}
                </div>
              )}
              <SeatMap sections={availToSections({ ...product, seat_availability: liveAvail })} stageLabel={product.seat_stage_label} stagePosition={product.seat_stage_position || "top"}
                selected={seats} onSeat={(tid, label, taken) => toggleSeat({ id: tid }, label, taken)}
                onSelectTable={(tid, labels) => setSeats((prev) => ({ ...prev, [tid]: Array.from(new Set([...(prev[tid] || []), ...labels])) }))} interactive />
              {tiers.filter((t) => (seats[t.id] || []).length > 0).map((t) => (
                <div key={t.id} className="rounded-xl border border-gray-200 p-4" data-testid={`tier-${t.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-[#16243b]">{t.name} <span className="text-[#8a97a8] text-sm">· {t.free || t.price <= 0 ? "Free" : `£${t.price.toFixed(2)}`}</span></p>
                    <span className="text-sm font-semibold text-[#16243b]" data-testid={`tier-seatcount-${t.id}`}>{(seats[t.id] || []).length} seat{(seats[t.id] || []).length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="space-y-2">
                    {(seats[t.id] || []).map((label) => (
                      <input key={label} placeholder={`${formatSeat(label)} — attendee full name`} data-testid={`attendee-seat-${t.id}-${label}`}
                        value={names[`${t.id}-seat-${label}`] || ""} onChange={(e) => setNames({ ...names, [`${t.id}-seat-${label}`]: e.target.value })}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* General admission: quantity steppers + names */}
          {!reserved && (
          <div className="space-y-3">
            {tiers.map((t) => {
              const rem = remaining(t);
              const soldOut = rem === 0;
              return (
                <div key={t.id} className="rounded-xl border border-gray-200 p-4" data-testid={`tier-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-[#16243b]">{t.name}</p>
                      <p className={`font-bold ${t.free || t.price <= 0 ? "text-green-600" : "text-[#2ec9c9]"}`}>{t.free || t.price <= 0 ? "Free" : `£${t.price.toFixed(2)}`}</p>
                      {t.capacity > 0 && <p className="text-xs text-[#8a97a8] mt-0.5">{soldOut ? "Sold out" : `${rem} left`}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="button" disabled={soldOut} onClick={() => setQ(t, -1)} data-testid={`tier-minus-${t.id}`}
                        className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center disabled:opacity-40 hover:border-[#2ec9c9]"><Minus size={15} /></button>
                      <span className="w-6 text-center font-semibold" data-testid={`tier-qty-${t.id}`}>{qty[t.id] || 0}</span>
                      <button type="button" disabled={soldOut} onClick={() => setQ(t, 1)} data-testid={`tier-plus-${t.id}`}
                        className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center disabled:opacity-40 hover:border-[#2ec9c9]"><Plus size={15} /></button>
                    </div>
                  </div>
                  {(qty[t.id] || 0) > 0 && (
                    <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                      {Array.from({ length: qty[t.id] }).map((_, i) => (
                        <input key={i} placeholder={`Attendee ${i + 1} full name`} data-testid={`attendee-${t.id}-${i}`}
                          value={names[`${t.id}-${i}`] || ""} onChange={(e) => setNames({ ...names, [`${t.id}-${i}`]: e.target.value })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )}

          {/* Buyer details */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">Your name</label>
              <input required value={buyer.buyer_name} onChange={(e) => setBuyer({ ...buyer, buyer_name: e.target.value })} data-testid="buyer-name"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">Your email</label>
              <input type="email" required value={buyer.buyer_email} onChange={(e) => setBuyer({ ...buyer, buyer_email: e.target.value })} data-testid="buyer-email"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">Mobile number</label>
              <input type="tel" required value={buyer.buyer_phone} onChange={(e) => setBuyer({ ...buyer, buyer_phone: e.target.value })} data-testid="buyer-phone" placeholder="e.g. 07123 456789"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
            </div>
          </div>

          {/* Discount */}
          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Promo code (optional)</label>
            <div className="flex gap-2">
              <input value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} placeholder="Enter code" data-testid="promo-input"
                className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] uppercase font-mono" />
              <button type="button" onClick={applyCode} disabled={checking} className="gp-btn-outline rounded-xl px-4 font-medium disabled:opacity-60">{checking ? "…" : "Apply"}</button>
            </div>
            {discount && <p className="text-sm text-green-600 mt-2 font-medium">Code {discount.code} applied.</p>}
          </div>

          <div className="bg-[#f6f8fb] rounded-xl p-4 text-sm text-[#5a6b80] flex items-start gap-2">
            <CreditCard size={18} className="text-[#2ec9c9] mt-0.5 shrink-0" />
            {grandTotal <= 0
              ? <span><strong className="text-[#16243b]">Free ticket</strong> — no payment needed. Enter each attendee's name and we'll email the ticket &amp; QR straight away.</span>
              : fees.live
                ? <span><strong className="text-[#16243b]">Secure checkout</strong> — your payment is processed securely by Stripe.</span>
                : <span><strong className="text-[#16243b]">Secure checkout</strong> — this organiser hasn't connected payments yet, so this is a demo (mocked) payment.</span>}
          </div>

          {totalTickets > 0 && (
            <div className="space-y-1.5 text-sm" data-testid="checkout-breakdown">
              <div className="flex items-center justify-between text-[#5a6b80]">
                <span>Subtotal ({totalTickets} ticket{totalTickets !== 1 ? "s" : ""})</span>
                <span>{total <= 0 ? "Free" : `£${total.toFixed(2)}`}</span>
              </div>
              {tableSaving > 0 && (
                <div className="flex items-center justify-between text-[#0f8a8a] font-medium" data-testid="checkout-table-saving">
                  <span>Whole-table saving</span>
                  <span>−£{tableSaving.toFixed(2)}</span>
                </div>
              )}
              {bookingFee > 0 && (
                <div className="flex items-center justify-between text-[#5a6b80]" data-testid="checkout-booking-fee">
                  <span>Booking fee</span>
                  <span>£{bookingFee.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between border-t border-gray-100 pt-4">
            <span className="text-[#8a97a8] text-sm">Total</span>
            <span className="text-2xl font-bold text-[#16243b]" data-testid="checkout-total">{grandTotal <= 0 ? "Free" : `£${grandTotal.toFixed(2)}`}</span>
          </div>
          <button disabled={loading || totalTickets === 0} data-testid="checkout-pay-btn" className={buttonColor ? "w-full rounded-xl py-3 font-medium disabled:opacity-60 flex items-center justify-center gap-2 transition-opacity hover:opacity-90" : "gp-btn-teal w-full rounded-xl py-3 font-medium disabled:opacity-60 flex items-center justify-center gap-2"} style={buttonColor || buttonTextColor ? { ...(buttonColor ? { background: buttonColor } : {}), ...(buttonTextColor ? { color: buttonTextColor } : {}) } : undefined}>
            {loading ? <Loader2 className="animate-spin" size={18} /> : null}
            {loading ? "Processing…" : (grandTotal <= 0 ? `Get free ticket${totalTickets !== 1 ? "s" : ""}` : `Pay £${grandTotal.toFixed(2)}`)}
          </button>
        </form>
      </div>
    </div>
  );
}
